import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useReducedMotionSafe } from "@/components/animation/AnimationSettings";
import { PALETTE } from "@/components/animation/MotionShared";

const DEFAULT_SECTORS = [
  { id: "tech",       label: "Technology",     v:  1.84 },
  { id: "energy",     label: "Energy",         v:  0.72 },
  { id: "financials", label: "Financials",     v:  0.31 },
  { id: "health",     label: "Healthcare",     v: -0.42 },
  { id: "consumer",   label: "Consumer",       v:  0.21 },
  { id: "industrials",label: "Industrials",    v: -0.18 },
  { id: "utilities",  label: "Utilities",      v: -0.95 },
  { id: "materials",  label: "Materials",      v:  0.65 },
  { id: "realestate", label: "Real Estate",    v: -0.61 },
  { id: "comm",       label: "Communications", v:  1.12 },
  { id: "crypto",     label: "Crypto Beta",    v:  2.45 },
  { id: "africa",     label: "Africa Macro",   v:  0.88 },
];

function tileColor(v) {
  // -3.. +3% range; clamp
  const n = Math.max(-3, Math.min(3, Number(v) || 0));
  if (n >= 0) {
    const a = Math.min(0.85, 0.15 + (n / 3) * 0.7);
    return `rgba(16,185,129,${a.toFixed(3)})`;
  }
  const a = Math.min(0.85, 0.15 + (Math.abs(n) / 3) * 0.7);
  return `rgba(239,68,68,${a.toFixed(3)})`;
}

export default function SectorHeatmap({ sectors, testid = "sector-heatmap", title = "Sector Heatmap" } = {}) {
  const reduce = useReducedMotionSafe();
  const data = useMemo(() => (sectors && sectors.length ? sectors : DEFAULT_SECTORS), [sectors]);
  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <strong style={S.title}>{title}</strong>
        <span style={S.note}>% change · mock data when live feed absent</span>
      </header>
      <div style={S.grid}>
        {data.map((sec, i) => (
          <motion.div
            key={sec.id}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={reduce ? { duration: 0 } : { delay: i * 0.04, duration: 0.4, ease: "easeOut" }}
            style={{ ...S.tile, background: tileColor(sec.v) }}
            data-testid={`${testid}-tile-${sec.id}`}
          >
            <span style={S.tileLbl}>{sec.label}</span>
            <span style={S.tileVal}>{(sec.v >= 0 ? "+" : "")}{Number(sec.v).toFixed(2)}%</span>
          </motion.div>
        ))}
      </div>
      <span style={S.compliance}>Educational research only · not a recommendation.</span>
    </section>
  );
}

const S = {
  shell: { background: PALETTE.glassDark, border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 6, color: PALETTE.slate3, maxWidth: "100%", boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  title: { color: PALETTE.goldBright, fontSize: 13 },
  note: { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 6 },
  tile: { borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)", padding: 8, display: "flex", flexDirection: "column", gap: 2, minHeight: 56 },
  tileLbl: { color: "#0c1a10", fontFamily: "'Figtree', system-ui, sans-serif", fontSize: 11, fontWeight: 800 },
  tileVal: { color: "#0c1a10", fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 800 },
  compliance: { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5 },
};
